import { useMutation, useQueryClient } from '@tanstack/react-query'
import { CheckinsService } from '@/shared/api/rest/services/checkins.service'
import { useAlertService } from '@/shared/lib/useAlertService'

type Args = { childId: string; branch?: string; session?: string }

/**
 * Check a child in or out without scanning a QR code.
 *
 * This is the override path for when the QR or the check-in code can't be used
 * — a missing code on the open record, a broken QR image, a lost printout.
 * `checkout-without-qr` skips code validation altogether, which is what makes
 * it the only way to release a child whose stored code is null.
 */
export const useManualCheckInOut = () => {
    const alertService = useAlertService()
    const queryClient = useQueryClient()

    const invalidate = () => {
        queryClient.invalidateQueries({ queryKey: ['checkout-queue'] })
        queryClient.invalidateQueries({ queryKey: ['child-checkin-history'] })
        queryClient.invalidateQueries({ queryKey: ['registry-parents'] })
    }

    const checkIn = useMutation({
        mutationFn: ({ childId, branch, session }: Args) =>
            CheckinsService.checkinWithoutQr({ child: childId, branch, session }),
        onSuccess: () => {
            alertService.success('Child checked in without QR')
            invalidate()
        },
        onError: (error: Error) =>
            alertService.error(error.message || 'Failed to check in child')
    })

    const checkOut = useMutation({
        mutationFn: ({ childId, branch, session }: Args) =>
            CheckinsService.checkoutWithoutQr({ childId, branch, session }),
        onSuccess: () => {
            alertService.success('Child checked out without QR')
            invalidate()
        },
        onError: (error: Error) =>
            alertService.error(error.message || 'Failed to check out child')
    })

    return {
        checkIn: checkIn.mutateAsync,
        checkOut: checkOut.mutateAsync,
        pending: checkIn.isPending || checkOut.isPending
    }
}
